import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { captionLength, platformMeta } from '../../platformMeta';
import { SocialPlatform } from '../../types';
import { REEL_CAPTION_MAX, strictestCaptionPlatform } from './reelReview';
import { reelApproveMessages } from './reelReviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelCaptionFieldProps {
  caption: string;
  onChange: (caption: string) => void;
  /** The picked channels. A caption over the limit of one gets a note: the server shortens it. */
  platforms: SocialPlatform[];
}

/**
 * The post caption of the approve sheet (design d1, d4): 2200 characters at most, not empty. When
 * the caption is longer than the limit of a picked channel (YouTube: 100), a note names the
 * strictest one: the server shortens the caption of that post at a word.
 */
const ReelCaptionFieldComponent: React.FC<ReelCaptionFieldProps> = ({ caption, onChange, platforms }) => {
  const intl = useIntl();
  const isTooLong = caption.length > REEL_CAPTION_MAX;
  const isEmpty = caption.trim() === '';
  const strictest = useMemo(() => strictestCaptionPlatform(platforms), [platforms]);
  const shortenedFor =
    !isTooLong && strictest !== undefined && captionLength(strictest, caption) > platformMeta[strictest].captionLimit
      ? platformMeta[strictest]
      : undefined;

  return (
    <div className={styles.field}>
      <div className={styles.countRow}>
        <label className={styles.label} htmlFor="reel-approve-caption">
          {intl.formatMessage(reelApproveMessages.caption)}
        </label>
        <span className={styles.secHint}>{intl.formatMessage(reelApproveMessages.captionHint)}</span>
      </div>
      <textarea
        id="reel-approve-caption"
        className={clsx(styles.textarea, (isTooLong || isEmpty) && styles.textareaBad)}
        value={caption}
        onChange={(event) => onChange(event.target.value)}
      />
      <div className={styles.countRow}>
        {isTooLong && (
          <span className={styles.bad}>
            {intl.formatMessage(reelApproveMessages.captionTooLong, { max: REEL_CAPTION_MAX })}
          </span>
        )}
        {isEmpty && <span className={styles.bad}>{intl.formatMessage(reelApproveMessages.captionEmpty)}</span>}
        {shortenedFor !== undefined && (
          <span className={styles.secHint}>
            {intl.formatMessage(reelApproveMessages.captionShortened, {
              platform: shortenedFor.name,
              max: shortenedFor.captionLimit,
            })}
          </span>
        )}
        <span className={clsx(styles.count, isTooLong && styles.bad)}>
          {intl.formatMessage(reelApproveMessages.captionCount, { count: caption.length, max: REEL_CAPTION_MAX })}
        </span>
      </div>
    </div>
  );
};

export const ReelCaptionField = TypedMemo(ReelCaptionFieldComponent);
